import React, { useRef, useState } from 'react';
import { X, Camera, RefreshCw, Loader2, QrCode } from 'lucide-react';
import { authFetch } from '@/utils/authFetch';
import { compressImageForVINScan } from '@/utils/imageCompression';

interface QRScannerProps {
  onScan: (result: string) => void;
  onClose: () => void;
}

type Mode = 'idle' | 'capturing' | 'analyzing' | 'result' | 'error';

// Below this confidence (or when the VIN check-digit validation fails) the
// user is asked to double-check the reading before it's used, rather than
// one-tap accepting a possibly-misread VIN.
const LOW_CONFIDENCE_THRESHOLD = 0.75;

export function QRScanner({ onScan, onClose }: QRScannerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [mode, setMode] = useState<Mode>('idle');
  const [error, setError] = useState<string | null>(null);
  const [vinResult, setVinResult] = useState<{ vin: string; confidence: number; reason: string; checksumValid?: boolean } | null>(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [verifiedAgainstPlate, setVerifiedAgainstPlate] = useState(false);

  const needsReview = !!vinResult && (vinResult.confidence < LOW_CONFIDENCE_THRESHOLD || vinResult.checksumValid === false);

  React.useEffect(() => {
    startCamera();
    return () => stopCamera();
  }, []);

  async function startCamera() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
        setCameraReady(true);
      }
    } catch (err) {
      console.error('Camera error:', err);
      setError('Could not access camera. Please check your camera permissions in browser settings and try again.');
    }
  }

  function stopCamera() {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  }

  async function captureAndAnalyze() {
    if (!videoRef.current || !canvasRef.current) return;
    setMode('capturing');

    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')?.drawImage(video, 0, 0);

    canvas.toBlob(async (blob) => {
      if (!blob) { setMode('idle'); return; }
      await analyzeImage(blob);
    }, 'image/jpeg', 0.92);
  }

  async function analyzeImage(blob: Blob) {
    setMode('analyzing');
    try {
      const compressed = await compressImageForVINScan(blob);
      const base64Data = await toBase64(compressed);

      let res: Response;
      try {
        res = await authFetch('/api/read-vin', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ base64Data, mediaType: 'image/jpeg' }),
        });
      } catch (networkErr: any) {
        // Network failure — show specific message
        setVinResult({ vin: '', confidence: 0, reason: `Network error: ${networkErr?.message ?? 'Could not reach server'}. Check your connection and try again.` });
        setMode('error');
        return;
      }

      if (!res.ok) {
        const errText = await res.text().catch(() => '');
        console.error('[VIN] API error:', res.status, errText);
        setVinResult({ vin: '', confidence: 0, reason: `Server error (${res.status}) — please try again.` });
        setMode('error');
        return;
      }

      let data: any;
      try {
        data = await res.json();
      } catch {
        setVinResult({ vin: '', confidence: 0, reason: 'Unexpected response from server — please try again.' });
        setMode('error');
        return;
      }

      if (data.found && data.vin && data.vin.length === 17) {
        setVinResult({ vin: data.vin, confidence: data.confidence, reason: data.reason, checksumValid: data.checksumValid });
        setMode('result');
      } else {
        setVinResult({
          vin: '',
          confidence: data.confidence ?? 0,
          reason: data.reason ?? 'No VIN detected — try moving closer and ensuring good lighting.',
        });
        setMode('error');
      }
    } catch (err: any) {
      console.error('[VIN] Unexpected error:', err);
      setVinResult({ vin: '', confidence: 0, reason: `Unexpected error: ${err?.message ?? 'Please try again.'}` });
      setMode('error');
    }
  }

  function handleConfirm() {
    if (vinResult?.vin && vinResult.vin.length === 17 && (!needsReview || verifiedAgainstPlate)) {
      stopCamera();
      onScan(vinResult.vin);
    }
  }

  function handleRetry() {
    setMode('idle');
    setVinResult(null);
    setVerifiedAgainstPlate(false);
    setError(null);
    if (!streamRef.current) startCamera();
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3">
        <div>
          <p className="text-sm font-semibold text-white">Scan Vehicle VIN</p>
          <p className="text-xs text-gray-400">Point camera at VIN plate and tap capture</p>
        </div>
        <button
          onClick={() => { stopCamera(); onClose(); }}
          className="rounded-full p-2 text-white hover:bg-white/10"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Camera / result area */}
      <div className="relative flex flex-1 flex-col items-center justify-center overflow-hidden">

        {/* Video feed */}
        <video
          ref={videoRef}
          className={`h-full w-full object-cover ${mode === 'result' || mode === 'error' ? 'opacity-20' : ''}`}
          playsInline
          muted
        />
        <canvas ref={canvasRef} className="hidden" />

        {/* Camera permission error */}
        {error && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-8 text-center">
            <Camera className="h-12 w-12 text-gray-500" />
            <p className="text-sm text-red-400">{error}</p>
            <button
              onClick={() => { stopCamera(); onClose(); }}
              className="rounded-lg bg-white/10 px-5 py-2.5 text-sm text-white hover:bg-white/20"
            >
              Close
            </button>
          </div>
        )}

        {/* Viewfinder overlay */}
        {cameraReady && (mode === 'idle' || mode === 'capturing') && !error && (
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <div className="absolute inset-0 bg-black/30" />
            {/* Wide frame for VIN plate */}
            <div className="relative w-[85%] h-28 rounded-xl border-2 border-wire3-green shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]">
              <div className="absolute -left-0.5 -top-0.5 h-5 w-5 rounded-tl-lg border-l-4 border-t-4 border-wire3-green" />
              <div className="absolute -right-0.5 -top-0.5 h-5 w-5 rounded-tr-lg border-r-4 border-t-4 border-wire3-green" />
              <div className="absolute -bottom-0.5 -left-0.5 h-5 w-5 rounded-bl-lg border-b-4 border-l-4 border-wire3-green" />
              <div className="absolute -bottom-0.5 -right-0.5 h-5 w-5 rounded-br-lg border-b-4 border-r-4 border-wire3-green" />
              {mode === 'idle' && (
                <div className="animate-scan absolute left-0 right-0 h-0.5 bg-wire3-green/80" />
              )}
            </div>
            <p className="relative mt-4 text-center text-xs text-white/80 px-6">
              Position the VIN plate within the frame — found on the dashboard, driver door jamb, or windshield strip
            </p>
          </div>
        )}

        {/* Analyzing overlay */}
        {mode === 'analyzing' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/70">
            <Loader2 className="h-10 w-10 animate-spin text-wire3-green" />
            <p className="text-sm font-medium text-white">Reading VIN...</p>
            <p className="text-xs text-gray-400">AI is analyzing the photo</p>
          </div>
        )}

        {/* Success result */}
        {mode === 'result' && vinResult && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-6">
            <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-gray-800 p-6 shadow-2xl">
              <div className="flex items-center gap-2 mb-3">
                <div className={`flex h-8 w-8 items-center justify-center rounded-full ${needsReview ? 'bg-status-warning/10' : 'bg-status-pass/10'}`}>
                  <QrCode className={`h-4 w-4 ${needsReview ? 'text-status-warning' : 'text-status-pass'}`} />
                </div>
                <p className="font-bold text-gray-900 dark:text-gray-100">VIN Detected</p>
                <span className="ml-auto text-xs text-gray-400">{Math.round(vinResult.confidence * 100)}% confidence</span>
              </div>

              <div className="rounded-lg bg-gray-50 dark:bg-gray-700 px-4 py-3 mb-4">
                <p className="text-xs text-gray-400 mb-1">Vehicle Identification Number</p>
                <p className="font-mono text-lg font-bold tracking-widest text-wire3-navy dark:text-wire3-green break-all">
                  {vinResult.vin}
                </p>
              </div>

              <p className="text-xs text-gray-500 dark:text-gray-400 mb-4 italic">{vinResult.reason}</p>

              {needsReview && (
                <label className="mb-4 flex items-start gap-2 rounded-lg border border-status-warning/30 bg-status-warning/5 p-3 text-xs text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={verifiedAgainstPlate}
                    onChange={(e) => setVerifiedAgainstPlate(e.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-status-warning"
                  />
                  <span>
                    <span className="font-semibold text-status-warning">Low-confidence read — </span>
                    Please check each character above against the physical VIN plate before confirming.
                  </span>
                </label>
              )}

              <div className="flex gap-2">
                <button
                  onClick={handleRetry}
                  className="flex-1 flex items-center justify-center gap-2 rounded-lg border border-gray-200 dark:border-gray-600 px-4 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <RefreshCw className="h-4 w-4" />
                  Retake
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={needsReview && !verifiedAgainstPlate}
                  className="flex-1 rounded-lg bg-wire3-green px-4 py-2.5 text-sm font-semibold text-wire3-navy hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Use This VIN
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Error / not found */}
        {mode === 'error' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-6">
            <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-gray-800 p-6 shadow-2xl">
              <div className="flex items-center gap-2 mb-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-status-fail/10">
                  <Camera className="h-4 w-4 text-status-fail" />
                </div>
                <p className="font-bold text-gray-900 dark:text-gray-100">VIN Not Found</p>
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                {vinResult?.reason ?? 'Could not read a VIN from the photo.'}
              </p>

              <p className="text-xs text-gray-400 mb-4">
                Tips: ensure good lighting, hold the camera steady, and fill the frame with the VIN plate. Try the dashboard VIN (visible through the windshield), the driver door jamb sticker, or the plate under the hood.
              </p>

              <button
                onClick={handleRetry}
                className="w-full flex items-center justify-center gap-2 rounded-lg bg-wire3-green px-4 py-2.5 text-sm font-semibold text-wire3-navy hover:opacity-90"
              >
                <RefreshCw className="h-4 w-4" />
                Try Again
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Capture button */}
      {(mode === 'idle' || mode === 'capturing') && !error && (
        <div className="flex items-center justify-center px-6 py-6 bg-black">
          <button
            onClick={captureAndAnalyze}
            disabled={mode === 'capturing' || !cameraReady}
            className="flex h-16 w-16 items-center justify-center rounded-full border-4 border-white bg-wire3-green disabled:opacity-50 active:scale-95 transition-transform"
          >
            {mode === 'capturing' ? (
              <Loader2 className="h-7 w-7 animate-spin text-wire3-navy" />
            ) : (
              <Camera className="h-7 w-7 text-wire3-navy" />
            )}
          </button>
        </div>
      )}
    </div>
  );
}

async function toBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
